import { useState, useEffect, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Archive,
  ArchiveRestore,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  Code2,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { DataTableBadge } from "@/components/ui/data-table-badge";
import { usePriceModelsStore } from "@/modules/priceModels/priceModelsStore";
import { PriceModelEditorSheet } from "@/modules/priceModels/PriceModelEditorSheet";
import { toast } from "sonner";
import type { PriceModel } from "@forgepoint/shared";

interface PriceModelsSettingsPanelProps {
  /** Whether the current user has admin privileges */
  isAdmin: boolean;
}

/**
 * Price models settings panel for managing Python pricing scripts.
 * Uses the standard settings UX pattern with section label, card rows,
 * skeleton loading, and sheet-based editing.
 * @param props - PriceModelsSettingsPanelProps
 * @returns Price models settings panel element
 */
export function PriceModelsSettingsPanel({
  isAdmin,
}: PriceModelsSettingsPanelProps) {
  const priceModelsMap = usePriceModelsStore((state) => state.priceModels);
  const listLoading = usePriceModelsStore((state) => state.listLoading);
  const listError = usePriceModelsStore((state) => state.listError);
  const fetchPriceModels = usePriceModelsStore(
    (state) => state.fetchPriceModels,
  );
  const deletePriceModel = usePriceModelsStore(
    (state) => state.deletePriceModel,
  );
  const updatePriceModel = usePriceModelsStore(
    (state) => state.updatePriceModel,
  );
  const priceModelLoadingStates = usePriceModelsStore(
    (state) => state.priceModelLoadingStates,
  );

  const [showArchived, setShowArchived] = useState(false);

  const priceModelsList = useMemo(
    () =>
      Array.from(priceModelsMap.values())
        .filter((pm) => showArchived || !pm.archivedAt)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [priceModelsMap, showArchived],
  );

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<"create" | "edit">("create");
  const [editingPriceModelId, setEditingPriceModelId] = useState<
    string | undefined
  >(undefined);
  const [deletingPriceModelId, setDeletingPriceModelId] = useState<
    string | null
  >(null);
  const [priceModelToArchive, setPriceModelToArchive] =
    useState<PriceModel | null>(null);

  useEffect(() => {
    fetchPriceModels({ includeArchived: true });
  }, [fetchPriceModels]);

  /**
   * Opens the editor sheet in create mode for a new price model.
   */
  const handleCreate = () => {
    setEditorMode("create");
    setEditingPriceModelId(undefined);
    setEditorOpen(true);
  };

  /**
   * Opens the editor sheet in edit mode for the specified price model.
   * @param id - The price model ID to edit
   */
  const handleEdit = (id: string) => {
    setEditorMode("edit");
    setEditingPriceModelId(id);
    setEditorOpen(true);
  };

  /**
   * Deletes the specified price model and shows a toast notification.
   * @param id - The price model ID to delete
   */
  const handleDelete = async (id: string) => {
    setDeletingPriceModelId(id);
    const result = await deletePriceModel(id);
    setDeletingPriceModelId(null);

    if (result.success) {
      toast.success("Price model deleted");
      return;
    }

    toast.error(result.error || "Failed to delete price model");
  };

  /**
   * Archives or unarchives the specified price model and shows a toast.
   * @param id - The price model ID to update
   * @param archived - Whether the model should be archived
   */
  const handleSetArchived = async (id: string, archived: boolean) => {
    const result = await updatePriceModel(id, { archived });

    if (result.priceModel) {
      toast.success(
        archived ? "Price model archived" : "Price model unarchived",
      );
      return;
    }

    toast.error(
      result.error ||
        `Failed to ${archived ? "archive" : "unarchive"} price model`,
    );
  };

  /**
   * Unarchives an archived price model directly, or opens the archive
   * confirmation dialog for an active one.
   * @param pm - The price model to toggle
   */
  const handleToggleArchived = (pm: PriceModel) => {
    if (pm.archivedAt) {
      handleSetArchived(pm.id, false);
      return;
    }
    setPriceModelToArchive(pm);
  };

  if (listLoading) {
    return <PriceModelsSkeleton />;
  }

  if (listError) {
    return (
      <Card className="border-destructive/40 shadow-sm">
        <CardContent className="py-6 text-sm text-destructive">
          {listError}
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-6 pt-4">
        {/* ── Section Header ── */}
        <div
          id="price-models-header"
          className="flex items-center justify-between scroll-mt-24"
        >
          <SectionLabel title="Price Models" />
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch
                id="show-archived-price-models"
                checked={showArchived}
                onCheckedChange={setShowArchived}
              />
              <Label
                htmlFor="show-archived-price-models"
                className="text-sm whitespace-nowrap"
              >
                Show Archived
              </Label>
            </div>
            {isAdmin && (
              <Button size="sm" onClick={handleCreate}>
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                New Price Model
              </Button>
            )}
          </div>
        </div>

        {/* ── Price Models List ── */}
        <Card className="shadow-sm overflow-hidden">
          {priceModelsList.length === 0 ? (
            <EmptyState isAdmin={isAdmin} onCreate={handleCreate} />
          ) : (
            <div className="divide-y">
              {priceModelsList.map((pm) => (
                <PriceModelRow
                  key={pm.id}
                  name={pm.name}
                  description={pm.description}
                  version={pm.version}
                  updatedAt={pm.updatedAt}
                  isArchived={!!pm.archivedAt}
                  isAdmin={isAdmin}
                  isDeleting={deletingPriceModelId === pm.id}
                  isArchiving={
                    priceModelLoadingStates.get(pm.id)?.isLoadingSave ?? false
                  }
                  onEdit={() => handleEdit(pm.id)}
                  onDelete={() => handleDelete(pm.id)}
                  onToggleArchived={() => handleToggleArchived(pm)}
                />
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* ── Archive Confirmation ── */}
      <AlertDialog
        open={!!priceModelToArchive}
        onOpenChange={(open) => {
          if (!open) {
            setPriceModelToArchive(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive price model</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to archive &ldquo;
              {priceModelToArchive?.name}&rdquo;? Archived price models are
              hidden from pickers and the agent, but items already linked to
              this model keep pricing with it. You can unarchive it later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (priceModelToArchive) {
                  handleSetArchived(priceModelToArchive.id, true);
                }
                setPriceModelToArchive(null);
              }}
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Editor Sheet ── */}
      {editorMode === "edit" && editingPriceModelId ? (
        <PriceModelEditorSheet
          open={editorOpen}
          onOpenChange={setEditorOpen}
          mode="edit"
          priceModelId={editingPriceModelId}
          onSaved={() => fetchPriceModels({ includeArchived: true })}
        />
      ) : (
        <PriceModelEditorSheet
          open={editorOpen}
          onOpenChange={setEditorOpen}
          mode="create"
          onSaved={() => fetchPriceModels({ includeArchived: true })}
        />
      )}
    </>
  );
}

// ── Local Components ──

interface PriceModelRowProps {
  /** Name of the price model */
  name: string;
  /** Optional description text */
  description: string | null;
  /** Current version number */
  version: number;
  /** Last updated timestamp */
  updatedAt: Date;
  /** Whether the price model is archived */
  isArchived: boolean;
  /** Whether the current user has admin privileges */
  isAdmin: boolean;
  /** Whether this row's delete action is in progress */
  isDeleting: boolean;
  /** Whether this row's archive/unarchive action is in progress */
  isArchiving: boolean;
  /** Handler for editing this price model */
  onEdit: () => void;
  /** Handler for deleting this price model */
  onDelete: () => void;
  /** Handler for the archive/unarchive toggle */
  onToggleArchived: () => void;
}

/**
 * A single price model row displayed in the settings card.
 * Shows name, description, version badge, and admin action buttons.
 * @param props - PriceModelRowProps
 * @returns Price model row element
 */
function PriceModelRow({
  name,
  description,
  version,
  updatedAt,
  isArchived,
  isAdmin,
  isDeleting,
  isArchiving,
  onEdit,
  onDelete,
  onToggleArchived,
}: PriceModelRowProps) {
  const formattedDate = useMemo(
    () => new Date(updatedAt).toLocaleDateString(),
    [updatedAt],
  );

  return (
    <div className="group px-4 transition-colors hover:bg-muted/30">
      <div className="flex items-center justify-between gap-4 py-3.5">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium leading-tight">{name}</p>
            <Badge
              variant="secondary"
              className="text-[10px] px-1.5 py-0 h-4 font-mono"
            >
              v{version}
            </Badge>
            {isArchived && (
              <DataTableBadge color="amber" className="text-[10px] px-1.5 py-0">
                Archived
              </DataTableBadge>
            )}
          </div>
          {description && (
            <p className="text-xs text-muted-foreground mt-0.5 max-w-md leading-snug">
              {description}
            </p>
          )}
          <p className="text-[11px] text-muted-foreground/60 mt-0.5">
            Updated {formattedDate}
          </p>
        </div>

        {isAdmin && (
          <div className="shrink-0 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={onEdit}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>Edit price model</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    disabled={isArchiving}
                    onClick={onToggleArchived}
                  >
                    {isArchiving ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : isArchived ? (
                      <ArchiveRestore className="h-3.5 w-3.5" />
                    ) : (
                      <Archive className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>{isArchived ? "Unarchive" : "Archive"} price model</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <AlertDialog>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        disabled={isDeleting}
                      >
                        {isDeleting ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </AlertDialogTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>Delete price model</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete price model</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete &ldquo;{name}&rdquo;? This
                    action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={onDelete}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>
    </div>
  );
}

interface EmptyStateProps {
  /** Whether the current user has admin privileges */
  isAdmin: boolean;
  /** Handler for creating a new price model */
  onCreate: () => void;
}

/**
 * Empty state shown when no price models exist.
 * Displays an icon, message, and an optional create button for admins.
 * @param props - EmptyStateProps
 * @returns Empty state element
 */
function EmptyState({ isAdmin, onCreate }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      <div className="rounded-full bg-muted p-3 mb-3">
        <Code2 className="h-5 w-5 text-muted-foreground" />
      </div>
      <p className="text-sm font-medium text-muted-foreground">
        No price models yet
      </p>
      <p className="text-xs text-muted-foreground/60 mt-1 max-w-xs">
        Price models are Python scripts that automatically calculate item
        operations, components, and margins.
      </p>
      {isAdmin && (
        <Button size="sm" variant="outline" className="mt-4" onClick={onCreate}>
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Create your first price model
        </Button>
      )}
    </div>
  );
}

/**
 * Skeleton loading state matching the PriceModelsSettingsPanel layout.
 * Shows a section header placeholder and multiple card rows.
 * @returns Skeleton layout element
 */
function PriceModelsSkeleton() {
  return (
    <div className="space-y-6 pt-4">
      <div className="flex items-center justify-between">
        <Skeleton className="h-5 w-28" />
        <Skeleton className="h-8 w-32 rounded-md" />
      </div>
      <Card className="shadow-sm overflow-hidden">
        <div className="divide-y">
          {Array.from({ length: 3 }).map((_, i) => (
            <SkeletonRow key={`pm-skeleton-${i}`} />
          ))}
        </div>
      </Card>
    </div>
  );
}

/**
 * Single skeleton row matching the PriceModelRow layout.
 * @returns Skeleton row element
 */
function SkeletonRow() {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3.5">
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="h-4 w-8 rounded-full" />
        </div>
        <Skeleton className="h-3 w-56" />
        <Skeleton className="h-2.5 w-24" />
      </div>
      <div className="flex items-center gap-1.5">
        <Skeleton className="h-8 w-8 rounded-md" />
        <Skeleton className="h-8 w-8 rounded-md" />
      </div>
    </div>
  );
}

/**
 * Standalone section label rendered above a Card as a lightweight heading.
 * @param props - Contains a title string
 * @returns Section label element
 */
function SectionLabel({ title }: { title: string }) {
  return <h3 className="text-lg font-semibold tracking-tight pl-2">{title}</h3>;
}
